import React from 'react';
import {useIntl} from 'react-intl';
import Util from '../../../../shared/util/util';
import styled, {css} from 'styled-components';
import {isBillableSplitAllowed} from '../../../../shared/util/cache/TimeRegistrationSettingsUtil';

export enum ALIGN {
	START = 'flex-start',
	END = 'flex-end',
}

const TimesheetTotalsStyle = styled.div<{align?: ALIGN}>`
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-grow: 1;
	justify-content: ${({align}) => align || ALIGN.START};
`;

const TimesheetTotalsColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	justify-content: center;
`;

const TimesheetTotalsText = styled.span<{align?: ALIGN; light?: boolean}>`
	display: inline-block;
	width: fit-content;
	line-height: 14px;
	text-align: left;
	white-space: nowrap;

	align-self: ${({align}) => align || ALIGN.START};

	${({light}) =>
		light &&
		css`
			color: #727483;
		`}
`;

type MainProps = {
	children: React.ReactNode;
	align?: ALIGN;
};
function TimesheetsTimeTableTotals({children, align}: MainProps): JSX.Element {
	return <TimesheetTotalsStyle align={align}>{children}</TimesheetTotalsStyle>;
}

type LabelsProps = {
	goToTimesheetsTab: () => void;
	align?: ALIGN;
};
function TimeSheetsTimeTableTotalsLabels({goToTimesheetsTab, align}: LabelsProps): JSX.Element {
	return (
		<TimesheetTotalsColumn onClick={goToTimesheetsTab}>
			<TimesheetTotalsText align={align}>Timesheet Total:</TimesheetTotalsText>
			{isBillableSplitAllowed() && (
				<TimesheetTotalsText align={align} light>
					Billable Total:{' '}
				</TimesheetTotalsText>
			)}
		</TimesheetTotalsColumn>
	);
}

type HoursProps = {
	minutesRegistered: number;
	workPeriodInMinutes: number;
	billableMinutesRegistered: number;
	goToTimesheetsTab: () => void;
	align?: ALIGN;
};
function TimesheetsTimeTableTotalsHours({
	minutesRegistered,
	workPeriodInMinutes,
	billableMinutesRegistered,
	goToTimesheetsTab,
	align,
}: HoursProps): JSX.Element {
	const intl = useIntl();
	const registeredTimeInHours = Util.convertMinutesToFullHour(minutesRegistered, intl, true);
	const workPeriodInHours = Util.convertMinutesToFullHour(workPeriodInMinutes, intl, true);
	const billableRegisteredTimeInHours = Util.convertMinutesToFullHour(billableMinutesRegistered, intl, true);
	return (
		<TimesheetTotalsColumn onClick={goToTimesheetsTab}>
			<TimesheetTotalsText align={align} data-cy={'timesheets-total-for-view'}>
				{registeredTimeInHours}/{workPeriodInHours}
			</TimesheetTotalsText>
			{isBillableSplitAllowed() && (
				<TimesheetTotalsText align={align} light>
					{billableRegisteredTimeInHours}
				</TimesheetTotalsText>
			)}
		</TimesheetTotalsColumn>
	);
}

type CombinedProps = {
	minutesRegistered: number;
	workPeriodInMinutes: number;
	billableMinutesRegistered: number;
	goToTimesheetsTab: () => void;
};
function TimesheetsTimeTableTotalsCombined({
	minutesRegistered,
	workPeriodInMinutes,
	billableMinutesRegistered,
	goToTimesheetsTab,
}: CombinedProps): JSX.Element {
	return (
		<>
			<TimeSheetsTimeTableTotalsLabels goToTimesheetsTab={goToTimesheetsTab} />
			<TimesheetsTimeTableTotalsHours
				minutesRegistered={minutesRegistered}
				workPeriodInMinutes={workPeriodInMinutes}
				billableMinutesRegistered={billableMinutesRegistered}
				goToTimesheetsTab={goToTimesheetsTab}
			/>
		</>
	);
}

TimesheetsTimeTableTotals.Labels = TimeSheetsTimeTableTotalsLabels;
TimesheetsTimeTableTotals.Hours = TimesheetsTimeTableTotalsHours;
TimesheetsTimeTableTotals.Combined = TimesheetsTimeTableTotalsCombined;

export default TimesheetsTimeTableTotals;
